import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FormButton } from '../components/FormButton'
import { resetAuthState } from '../services/api-client'
import { consumeSessionId, debugDumpAuthState } from '../utils/session'

const WORKOS_CLIENT_ID = import.meta.env.VITE_WORKOS_CLIENT_ID || ''

type ErrorReason = 'account_blocked' | 'no_tenant' | 'backend_unavailable' | 'session_expired' | null

function useErrorReason(): { reason: ErrorReason; message: string | null } {
  const [state] = useState(() => {
    const r = sessionStorage.getItem('logout_reason') as ErrorReason
    const m = sessionStorage.getItem('logout_message')
    if (r) sessionStorage.removeItem('logout_reason')
    if (m) sessionStorage.removeItem('logout_message')
    sessionStorage.removeItem('intentional_logout')
    resetAuthState()
    return { reason: r, message: m }
  })
  return state
}

const ERROR_CONFIG: Record<string, { title: string; defaultMessage: string; color: 'red' | 'amber' }> = {
  account_blocked: {
    title: 'Account unavailable',
    defaultMessage: 'Your account is no longer active. Please contact your administrator.',
    color: 'red',
  },
  no_tenant: {
    title: 'No organisation found',
    defaultMessage: 'Your account is not associated with an organisation. Please contact your administrator.',
    color: 'amber',
  },
  backend_unavailable: {
    title: 'Something went wrong',
    defaultMessage: 'We could not complete sign-in. Please try again.',
    color: 'amber',
  },
  session_expired: {
    title: 'Session expired',
    defaultMessage: 'Your session has expired. Please sign in again.',
    color: 'amber',
  },
}

function ErrorIcon({ color }: { color: 'red' | 'amber' }) {
  if (color === 'red') {
    return (
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
        <svg className="h-8 w-8 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      </div>
    )
  }

  return (
    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
      <svg className="h-8 w-8 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    </div>
  )
}

export function AuthErrorPage() {
  const navigate = useNavigate()
  const { reason, message } = useErrorReason()
  const [signingOut, setSigningOut] = useState(false)

  const config = reason ? ERROR_CONFIG[reason] : null
  const title = config?.title ?? 'Something went wrong'
  const displayMessage = message || config?.defaultMessage || 'An unexpected error occurred. Please try again.'
  const color = config?.color ?? 'amber'

  // If no reason was set (direct visit to /auth/error), just go to login
  if (!reason) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
          <p className="text-gray-500">Nothing to see here.</p>
          <button
            onClick={() => navigate('/auth/login', { replace: true })}
            className="mt-4 text-sm text-brand-primary hover:underline"
          >
            Go to sign in
          </button>
        </div>
      </div>
    )
  }

  const handleBackToSignIn = async () => {
    setSigningOut(true)
    resetAuthState()

    console.log('[auth:AuthErrorPage] handleBackToSignIn clicked')
    debugDumpAuthState('AuthErrorPage handleBackToSignIn')

    const sessionId = consumeSessionId()

    if (sessionId) {
      console.log('[auth:AuthErrorPage] session_id found — redirecting to WorkOS logout', { sessionId })
      const returnTo = encodeURIComponent(`${window.location.origin}/auth/login`)
      const logoutUrl = `https://api.workos.com/user_management/sessions/logout?client_id=${WORKOS_CLIENT_ID}&session_id=${sessionId}&return_to=${returnTo}`
      console.log('[auth:AuthErrorPage] logout URL', logoutUrl)
      window.location.href = logoutUrl
    } else {
      console.warn('[auth:AuthErrorPage] no session_id found — going straight to /auth/login (cookie may survive)')
      window.location.href = '/auth/login'
    }
  }

  const handleTryAgain = () => {
    navigate('/', { replace: true })
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm text-center">
        <ErrorIcon color={color} />

        <h1 className="mt-6 text-2xl font-bold tracking-tight text-gray-900">
          {title}
        </h1>

        <p className="mt-3 text-sm leading-6 text-gray-600">
          {displayMessage}
        </p>

        <div className="mt-8 space-y-3">
          <FormButton
            onClick={handleBackToSignIn}
            variant="primary"
            loading={signingOut}
            fullWidth
          >
            Back to sign in
          </FormButton>

          {(reason === 'backend_unavailable' || reason === 'session_expired') && (
            <FormButton
              onClick={handleTryAgain}
              variant="secondary"
              fullWidth
            >
              Try again
            </FormButton>
          )}
        </div>
      </div>
    </div>
  )
}
